<template>
  <v-card flat>
    <div class="d-flex align-center mb-3">
      <h3>Recurring Tasks by User</h3>
      <v-spacer></v-spacer>
      <v-btn-toggle v-model="viewMode" mandatory dense class="mr-3">
        <v-btn small value="card">
          <v-icon small left>mdi-view-grid</v-icon>
          Card
        </v-btn>
        <v-btn small value="table">
          <v-icon small left>mdi-table</v-icon>
          Table
        </v-btn>
      </v-btn-toggle>
      <v-btn icon @click="loadRecurringTasks" :loading="loading">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <!-- Filters -->
    <v-row dense class="mb-3">
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="search"
          label="Search tasks..."
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="3">
        <v-select
          v-model="recurrenceFilter"
          :items="recurrenceOptions"
          label="Recurrence Type"
          clearable
          dense
          outlined
        ></v-select>
      </v-col>
      <v-col cols="12" sm="3">
        <v-select
          v-model="categoryFilter"
          :items="categoryOptions"
          label="Category"
          clearable
          dense
          outlined
        ></v-select>
      </v-col>
      <v-col cols="12" sm="2">
        <v-select
          v-model="priorityFilter"
          :items="priorityOptions"
          label="Priority"
          clearable
          dense
          outlined
        ></v-select>
      </v-col>
    </v-row>

    <!-- Summary chips -->
    <div class="mb-3" v-if="recurring_tasks.length > 0">
      <v-chip class="mr-2" color="primary" outlined small>
        {{ groupedUsers.length }} Users
      </v-chip>
      <v-chip class="mr-2" color="blue" outlined small>
        {{ filteredTasks.length }} Recurring Tasks
      </v-chip>
      <v-chip class="mr-2" color="green" outlined small>
        {{ filteredTasks.filter((t) => t.recurrence_type === "daily").length }}
        Daily
      </v-chip>
      <v-chip class="mr-2" color="orange" outlined small>
        {{ filteredTasks.filter((t) => t.recurrence_type === "weekly").length }}
        Weekly
      </v-chip>
      <v-chip color="purple" outlined small>
        {{
          filteredTasks.filter((t) => t.recurrence_type === "monthly").length
        }}
        Monthly
      </v-chip>
    </div>

    <!-- No data -->
    <v-alert v-if="!loading && recurring_tasks.length === 0" type="info" text>
      No recurring tasks found. Recurring tasks (daily, weekly, monthly) that
      are assigned to users will appear here.
    </v-alert>

    <!-- ==================== CARD VIEW ==================== -->
    <div v-if="viewMode === 'card' && groupedUsers.length > 0">
      <v-row>
        <v-col
          v-for="group in groupedUsers"
          :key="group.userId"
          cols="12"
          sm="6"
          lg="6"
        >
          <v-card outlined class="routine-card fill-height">
            <!-- User header -->
            <v-card-title class="py-3 routine-card-header">
              <v-avatar color="primary" size="36" class="mr-3">
                <span class="white--text subtitle-2">
                  {{ userInitials(group.userName) }}
                </span>
              </v-avatar>
              <div>
                <div class="subtitle-1 font-weight-bold">
                  {{ group.userName }}
                  <span
                    v-if="group.username"
                    class="caption grey--text font-weight-regular"
                  >
                    ({{ group.username }})
                  </span>
                </div>
                <div v-if="group.branchName" class="caption grey--text mt-n1">
                  {{ group.branchName }}
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-chip small color="primary" outlined>
                {{ group.tasks.length }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Routine timeline -->
            <v-card-text class="pa-0 routine-list">
              <div
                v-for="(task, idx) in sortedByTime(group.tasks)"
                :key="task.id"
                class="routine-item"
                :class="{
                  'routine-item--last': idx === group.tasks.length - 1,
                }"
              >
                <div class="d-flex align-start">
                  <!-- Time column -->
                  <div class="routine-time text-center">
                    <div
                      class="font-weight-bold"
                      :class="task.scheduled_time ? '' : 'grey--text'"
                    >
                      {{
                        task.scheduled_time
                          ? formatTime(task.scheduled_time)
                          : "No time"
                      }}
                    </div>
                  </div>

                  <!-- Timeline dot -->
                  <div class="routine-dot-col">
                    <div
                      class="routine-dot"
                      :style="{
                        backgroundColor: priorityDotColor(task.priority),
                      }"
                    ></div>
                    <div
                      v-if="idx < group.tasks.length - 1"
                      class="routine-line"
                    ></div>
                  </div>

                  <!-- Task detail -->
                  <div class="routine-detail flex-grow-1">
                    <div class="d-flex align-center">
                      <span class="body-2 font-weight-medium">
                        {{ task.title }}
                      </span>
                      <v-spacer></v-spacer>
                      <v-btn
                        icon
                        x-small
                        color="primary"
                        @click="$emit('edit', task)"
                        class="ml-1"
                      >
                        <v-icon x-small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        x-small
                        color="error"
                        @click="$emit('cancel', task)"
                      >
                        <v-icon x-small>mdi-cancel</v-icon>
                      </v-btn>
                    </div>

                    <div class="d-flex align-center flex-wrap mt-1">
                      <v-chip
                        x-small
                        :color="recurrenceColor(task.recurrence_type)"
                        outlined
                        class="mr-1 mb-1"
                      >
                        {{ recurrenceLabel(task.recurrence_type) }}
                      </v-chip>
                      <v-chip
                        x-small
                        :color="priorityColor(task.priority)"
                        dark
                        class="mr-1 mb-1"
                      >
                        {{ priorityLabel(task.priority) }}
                      </v-chip>
                      <v-chip
                        v-if="task.category"
                        x-small
                        outlined
                        class="mr-1 mb-1"
                      >
                        {{ task.category }}
                      </v-chip>
                    </div>

                    <div class="caption grey--text mt-1">
                      {{ scheduleDescription(task) }}
                      <span v-if="task.start_date">
                        &middot;
                        {{ task.start_date.substr(0, 10) }}
                        <template v-if="task.end_date">
                          to {{ task.end_date.substr(0, 10) }}
                        </template>
                      </span>
                    </div>

                    <div
                      v-if="task.description"
                      class="caption grey--text text--darken-1 mt-1"
                      style="
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        max-width: 100%;
                      "
                    >
                      {{ task.description }}
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- ==================== TABLE VIEW ==================== -->
    <v-expansion-panels
      v-if="viewMode === 'table' && groupedUsers.length > 0"
      v-model="openPanels"
      multiple
    >
      <v-expansion-panel v-for="group in groupedUsers" :key="group.userId">
        <v-expansion-panel-header>
          <div class="d-flex align-center">
            <v-icon class="mr-2" color="primary">mdi-account</v-icon>
            <span class="font-weight-bold">{{ group.userName }}</span>
            <span v-if="group.username" class="caption grey--text ml-1">
              ({{ group.username }})
            </span>
            <span v-if="group.branchName" class="caption grey--text ml-2">
              ({{ group.branchName }})
            </span>
            <v-spacer></v-spacer>
            <v-chip small color="primary" class="mr-4">
              {{ group.tasks.length }} task{{
                group.tasks.length !== 1 ? "s" : ""
              }}
            </v-chip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :items="sortedByTime(group.tasks)"
            :headers="tableHeaders"
            :items-per-page="10"
            dense
          >
            <template v-slot:item.priority="{ item }">
              <v-chip :color="priorityColor(item.priority)" small dark>
                {{ priorityLabel(item.priority) }}
              </v-chip>
            </template>

            <template v-slot:item.recurrence_type="{ item }">
              <v-chip
                :color="recurrenceColor(item.recurrence_type)"
                small
                outlined
              >
                {{ recurrenceLabel(item.recurrence_type) }}
              </v-chip>
            </template>

            <template v-slot:item.schedule="{ item }">
              <span class="caption">{{ scheduleDescription(item) }}</span>
            </template>

            <template v-slot:item.date_range="{ item }">
              <span class="caption">
                {{ item.start_date ? item.start_date.substr(0, 10) : "-" }}
                <span v-if="item.end_date">
                  to {{ item.end_date.substr(0, 10) }}
                </span>
                <span v-else class="grey--text">(no end date)</span>
              </span>
            </template>

            <template v-slot:item.scheduled_time="{ item }">
              {{ item.scheduled_time ? formatTime(item.scheduled_time) : "-" }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon small color="primary" @click="$emit('edit', item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small color="error" @click="$emit('cancel', item)">
                <v-icon small>mdi-cancel</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      loading: false,
      viewMode: "card",
      search: "",
      recurrenceFilter: null,
      categoryFilter: null,
      priorityFilter: null,
      openPanels: [],
      tableHeaders: [
        { text: "Time", value: "scheduled_time", width: "100" },
        { text: "Title", value: "title" },
        { text: "Category", value: "category", width: "120" },
        { text: "Priority", value: "priority", width: "110" },
        { text: "Recurrence", value: "recurrence_type", width: "110" },
        {
          text: "Schedule",
          value: "schedule",
          sortable: false,
          width: "180",
        },
        {
          text: "Date Range",
          value: "date_range",
          sortable: false,
          width: "200",
        },
        { text: "Actions", value: "actions", sortable: false, width: "100" },
      ],
      recurrenceOptions: [
        { text: "Daily", value: "daily" },
        { text: "Weekly", value: "weekly" },
        { text: "Monthly", value: "monthly" },
      ],
      priorityOptions: [
        { text: "Basic", value: "basic" },
        { text: "Urgent", value: "urgent" },
        { text: "Very Urgent", value: "very_urgent" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      recurring_tasks: "reminder/recurring_tasks",
    }),
    categoryOptions() {
      const cats = [
        ...new Set(this.recurring_tasks.map((t) => t.category).filter(Boolean)),
      ];
      return cats.sort();
    },
    filteredTasks() {
      let tasks = [...this.recurring_tasks];

      if (this.recurrenceFilter) {
        tasks = tasks.filter(
          (t) => t.recurrence_type === this.recurrenceFilter
        );
      }
      if (this.categoryFilter) {
        tasks = tasks.filter((t) => t.category === this.categoryFilter);
      }
      if (this.priorityFilter) {
        tasks = tasks.filter((t) => t.priority === this.priorityFilter);
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        tasks = tasks.filter(
          (t) =>
            (t.title && t.title.toLowerCase().includes(q)) ||
            (t.description && t.description.toLowerCase().includes(q)) ||
            (t.category && t.category.toLowerCase().includes(q))
        );
      }
      return tasks;
    },
    groupedUsers() {
      const map = {};
      this.filteredTasks.forEach((task) => {
        const user = task.assigned_to;
        const userId = user && typeof user === "object" ? user.id : user;
        const userName =
          user && typeof user === "object" ? user.full_name : "Unknown User";
        const username = user && typeof user === "object" ? user.username : "";
        const branchName =
          user && typeof user === "object" && user.branch
            ? user.branch.branch_name
            : "";

        if (!map[userId]) {
          map[userId] = {
            userId,
            userName,
            username,
            branchName,
            tasks: [],
          };
        }
        map[userId].tasks.push(task);
      });

      return Object.values(map).sort((a, b) =>
        a.userName.localeCompare(b.userName)
      );
    },
  },
  created() {
    this.loadRecurringTasks();
  },
  methods: {
    ...mapActions({
      get_recurring_tasks: "reminder/get_recurring_tasks",
    }),
    async loadRecurringTasks() {
      this.loading = true;
      try {
        await this.get_recurring_tasks();
        this.openPanels = this.groupedUsers.map((_, i) => i);
      } finally {
        this.loading = false;
      }
    },
    sortedByTime(tasks) {
      return [...tasks].sort((a, b) => {
        const timeA = a.scheduled_time || "99:99:99";
        const timeB = b.scheduled_time || "99:99:99";
        return timeA.localeCompare(timeB);
      });
    },
    userInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .toUpperCase()
        .substr(0, 2);
    },
    priorityColor(p) {
      return p === "very_urgent" ? "red" : p === "urgent" ? "orange" : "blue";
    },
    priorityDotColor(p) {
      return p === "very_urgent"
        ? "#F44336"
        : p === "urgent"
        ? "#FF9800"
        : "#1976D2";
    },
    priorityLabel(p) {
      return p === "very_urgent"
        ? "Very Urgent"
        : p === "urgent"
        ? "Urgent"
        : "Basic";
    },
    recurrenceColor(r) {
      return r === "daily" ? "green" : r === "weekly" ? "orange" : "purple";
    },
    recurrenceLabel(r) {
      const map = { daily: "Daily", weekly: "Weekly", monthly: "Monthly" };
      return map[r] || r;
    },
    parseDays(raw) {
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        try {
          return JSON.parse(raw);
        } catch (_) {
          return [];
        }
      }
      return [];
    },
    scheduleDescription(task) {
      const days = this.parseDays(task.recurrence_days);
      const dayNames = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      if (task.recurrence_type === "daily") {
        if (Array.isArray(days) && days.length > 0) {
          return days.map((d) => dayNames[Number(d)] || d).join(", ");
        }
        return "Every day";
      }
      if (task.recurrence_type === "weekly" && Array.isArray(days)) {
        if (days.length === 0) return "-";
        return days.map((d) => dayNames[Number(d)] || d).join(", ");
      }
      if (task.recurrence_type === "monthly" && Array.isArray(days)) {
        if (days.length === 0) return "-";
        return "Day " + days.map(Number).join(", ");
      }
      return "-";
    },
    formatTime(time) {
      if (!time) return "";
      const parts = time.split(":");
      let h = parseInt(parts[0]);
      const m = parts[1];
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return h + ":" + m + " " + ampm;
    },
  },
};
</script>

<style scoped>
.routine-card-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
}

.routine-list {
  max-height: 500px;
  overflow-y: auto;
}

.routine-item {
  padding: 12px 16px;
  transition: background-color 0.15s;
}

.routine-item:hover {
  background-color: #f9fafb;
}

.routine-item--last .routine-line {
  display: none;
}

.routine-time {
  min-width: 70px;
  padding-top: 2px;
  font-size: 0.8rem;
}

.routine-dot-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 24px;
  margin: 0 10px;
  padding-top: 4px;
}

.routine-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.routine-line {
  width: 2px;
  flex-grow: 1;
  min-height: 30px;
  background-color: #e0e0e0;
  margin-top: 4px;
}

.routine-detail {
  padding-bottom: 4px;
}
</style>
